//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import PartialCuid from '@models/PartialCuid';
import { ShareDataActionType } from '@share/actions/shareDataActions';
import { SHARE_ACTIONS } from '@share/constants/shareConstants';
import {
  ClientRelationshipType,
  IShareData,
  IShareModerationStats,
  ISharePublisherClient,
  IShareTaggedProductStats,
} from '@share/models/shareStateModel';

const initialState: IShareData = {
  flags: {
    isDoneFetchingMedia: false,
    isFetchingFilteredMedia: false,
    isFetchingMediaAndStats: true, // Initialize to true to show a loading spinner on first page load
    isFetchingMediaByCuid: false,
    isFetchingNextMedia: false,
  },
  items: [],
  moderationStats: {} as IShareModerationStats,
  publisherClients: [],
  taggedProductsStats: [] as IShareTaggedProductStats[],
  totalItemCount: 0,
};

export default function ShareDataReducer(
  state: IShareData = initialState,
  action: ShareDataActionType,
): IShareData {
  switch (action.type) {
    case SHARE_ACTIONS.GET_PUBLISHER_CLIENTS:
      return {
        ...state,
        publisherClients: action.publisherClients,
      };
    case SHARE_ACTIONS.GET_MODERATION_STATS:
      return {
        ...state,
        moderationStats: action.stats,
      };
    case SHARE_ACTIONS.RECEIVE_FILTERED_SHARED_MEDIA_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingFilteredMedia: false,
        },
        items: action.items,
        totalItemCount: action.totalItemCount,
      };
    case SHARE_ACTIONS.RECEIVE_NEXT_SHARED_MEDIA_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingNextMedia: false,
        },
        items: [...state.items, ...action.items],
      };
    case SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_AND_STATS_SUCCEEDED:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMediaAndStats: false,
        },
        items: action.items,
        totalItemCount: action.totalItemCount,
      };
    case SHARE_ACTIONS.RECEIVE_SHARED_MEDIA_BY_CUID_SUCCEEDED: {
      const newItem = action.items[0];
      let wasExistingItem = false;

      const updatedItems = state.items.map((item) => {
        if (item.networkItem.id === newItem.networkItem.id) {
          wasExistingItem = true;
          return newItem;
        }
        return item;
      });

      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMediaByCuid: false,
        },
        items: wasExistingItem
          ? updatedItems
          : [...state.items, ...action.items],
      };
    }
    case SHARE_ACTIONS.REMOVE_SHARED_ITEMS: {
      const sharedItemsWithoutRemoved = state.items.filter((item) => {
        return !action.cuids.includes(
          PartialCuid.buildFromNetworkItem(item.networkItem),
        );
      });

      return {
        ...state,
        items: sharedItemsWithoutRemoved,
      };
    }
    case SHARE_ACTIONS.REQUEST_FILTERED_SHARED_MEDIA:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingFilteredMedia: true,
        },
      };
    case SHARE_ACTIONS.REQUEST_NEXT_SHARED_MEDIA:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingNextMedia: true,
        },
      };
    case SHARE_ACTIONS.REQUEST_SHARED_MEDIA_AND_STATS:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMediaAndStats: true,
        },
      };
    case SHARE_ACTIONS.REQUEST_SHARED_MEDIA_BY_CUID:
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMediaByCuid: true,
        },
      };
    case SHARE_ACTIONS.SET_SHARED_MEDIA_LAST_FETCH:
      return {
        ...state,
        flags: {
          ...state.flags,
          isDoneFetchingMedia: action.isLastFetch,
        },
      };
    case SHARE_ACTIONS.RESET_SHARED_MEDIA:
      return {
        ...state,
        flags: {
          ...state.flags,
          isDoneFetchingMedia: false,
        },
        items: [],
      };
    case SHARE_ACTIONS.GET_TAGGED_PRODUCTS_STATS:
      return {
        ...state,
        taggedProductsStats: action.taggedProductsStats,
      };
    case SHARE_ACTIONS.GET_BAZAARVOICE_DESTINATIONS: {
      const publisherClients: ISharePublisherClient[] = action.clients.map<ISharePublisherClient>(
        (bazaarvoiceDestClient) => ({
          id: bazaarvoiceDestClient.id,
          name: bazaarvoiceDestClient.name,
          isModerationServices: false,
          isModerationServicesPublisherClient: false,
          isCopyCreatorLabelsEnabled: false,
          relationshipType: ClientRelationshipType.CONNECT,
          isCreatorRightsApprovalRequired: false,
        }),
      );

      return {
        ...state,
        publisherClients,
      };
    }
    case SHARE_ACTIONS.SELECT_DESTINATION_CLIENT: {
      const existingPublisherClients = state.publisherClients;

      // Re-sort the list of publishers such that the selected id
      //  is now first. This also implies that we don't have to check
      //  whether or not the publisher client id supplied in the action
      //  actually exists.
      const newPublisherClients = existingPublisherClients.sort(
        (publisherA, publisherB) => {
          // publisherA is the client we want to be first, give it a lower index than B
          if (publisherA.id === action.clientId) {
            return -1;
          } else if (publisherB.id === action.clientId) {
            return 1;
          } else {
            return 0;
          }
        },
      );

      return {
        ...state,
        publisherClients: [...newPublisherClients],
      };
    }
    default:
      return state;
  }
}
